import { Button, Popover, PopoverContent, PopoverTrigger } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { type ReactNode, useEffect, useState } from 'react'
import Cookies, { type CookieSetOptions } from 'universal-cookie'
import LogoSymbol from '../logo-symbol/logo-symbol'

interface SFHelpPopupProps {
  children: ReactNode
  cookieName: string
  content: ReactNode
  title?: string
  maxAgeSeconds?: number
  neverExpire?: boolean
  autoShow?: boolean
  showOnHover?: boolean
  showOnClick?: boolean
  positioning?: {
    placement?:
      | 'top'
      | 'bottom'
      | 'left'
      | 'right'
      | 'top-start'
      | 'top-end'
      | 'bottom-start'
      | 'bottom-end'
      | 'left-start'
      | 'left-end'
      | 'right-start'
      | 'right-end'
    gutter?: number
  }
  /** Popover panel width (CSS length, e.g. `300px`). */
  width?: string
  /** Extra classes for the popover panel (e.g. theme overrides). */
  contentClassName?: string
  showLogo?: boolean
  dismissText?: string
  actionText?: string
  onAction?: () => void
  onDismiss?: () => void
}

function placementToPopoverProps(positioning: SFHelpPopupProps['positioning']): {
  side: 'top' | 'bottom' | 'left' | 'right' | 'inline-end' | 'inline-start'
  align: 'start' | 'center' | 'end'
  sideOffset: number
} {
  const placement = positioning?.placement ?? 'bottom'
  const sideOffset = positioning?.gutter ?? 4

  const map: Record<
    NonNullable<SFHelpPopupProps['positioning']>['placement'],
    { side: 'top' | 'bottom' | 'left' | 'right'; align: 'start' | 'center' | 'end' }
  > = {
    top: { side: 'top', align: 'center' },
    bottom: { side: 'bottom', align: 'center' },
    left: { side: 'left', align: 'center' },
    right: { side: 'right', align: 'center' },
    'top-start': { side: 'top', align: 'start' },
    'top-end': { side: 'top', align: 'end' },
    'bottom-start': { side: 'bottom', align: 'start' },
    'bottom-end': { side: 'bottom', align: 'end' },
    'left-start': { side: 'left', align: 'start' },
    'left-end': { side: 'left', align: 'end' },
    'right-start': { side: 'right', align: 'start' },
    'right-end': { side: 'right', align: 'end' },
  }

  const resolved = map[placement] ?? { side: 'bottom', align: 'center' }
  return { ...resolved, sideOffset }
}

export const dismissHelpPopup = (cookieName: string, maxAgeSeconds?: number, neverExpire?: boolean) => {
  const cookies = new Cookies()
  const cookieOptions: CookieSetOptions = { path: '/' }

  if (neverExpire) {
    cookieOptions.maxAge = 60 * 60 * 24 * 365 * 10
  } else if (maxAgeSeconds) {
    cookieOptions.maxAge = maxAgeSeconds
  }

  cookies.set(cookieName, 'true', cookieOptions)
}

const SFHelpPopup = ({
  children,
  cookieName,
  content,
  title,
  maxAgeSeconds = 60 * 60 * 24 * 14,
  neverExpire = false,
  autoShow = true,
  showOnHover = false,
  showOnClick = false,
  positioning = { placement: 'bottom' },
  width = '300px',
  contentClassName,
  showLogo = true,
  dismissText = 'Dismiss',
  actionText,
  onAction,
  onDismiss,
}: SFHelpPopupProps) => {
  const [showPopover, setShowPopover] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const cookies = new Cookies()
  const { side, align, sideOffset } = placementToPopoverProps(positioning)

  useEffect(() => {
    const dismissedPopover = cookies.get(cookieName)
    if (!dismissedPopover && autoShow) {
      setShowPopover(true)
      setIsOpen(true)
    } else if (!dismissedPopover) {
      setShowPopover(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookieName, autoShow])

  const handleDismiss = () => {
    dismissHelpPopup(cookieName, maxAgeSeconds, neverExpire)
    setShowPopover(false)
    setIsOpen(false)
    onDismiss?.()
  }

  const handleAction = () => {
    handleDismiss()
    onAction?.()
  }

  const handleTriggerClick = () => {
    if (showOnClick && showPopover) {
      setIsOpen(!isOpen)
    }
  }

  const handleTriggerHover = () => {
    if (showOnHover && showPopover) {
      setIsOpen(true)
    }
  }

  const handleTriggerLeave = () => {
    if (showOnHover && showPopover && !autoShow) {
      setIsOpen(false)
    }
  }

  const handleOpenChange = (open: boolean, eventDetails: { cancel: () => void; reason: string }) => {
    if (!open && autoShow) {
      if (eventDetails.reason === 'outside-press' || eventDetails.reason === 'escape-key') {
        eventDetails.cancel()
        return
      }
    }
    setIsOpen(open)
  }

  if (!showPopover) return <>{children}</>

  return (
    <Popover onOpenChange={handleOpenChange} open={isOpen}>
      <PopoverTrigger
        nativeButton={false}
        onClick={showOnClick ? handleTriggerClick : undefined}
        onMouseEnter={showOnHover ? handleTriggerHover : undefined}
        onMouseLeave={showOnHover ? handleTriggerLeave : undefined}
        render={<span className="relative inline-flex" />}
      >
        {children}
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className={cn('gap-0 p-4', contentClassName)}
        positionerClassName="shadcn-light-scope"
        side={side}
        sideOffset={sideOffset}
        style={{ width }}
      >
        <div className="flex flex-col gap-3">
          {showLogo || title ? (
            <div className="flex w-full flex-wrap items-center gap-2">
              {showLogo ? (
                <LogoSymbol imageClassName="w-[22px] animate-[sf-waveplane_2.5s_ease-in-out_infinite]" />
              ) : null}
              {title ? <div className="font-semibold text-sm">{title}</div> : null}
            </div>
          ) : null}
          <div className="text-sm">{content}</div>
          <div
            className={cn('flex w-full items-center gap-2', actionText && onAction ? 'justify-between' : 'justify-end')}
          >
            {actionText && onAction ? (
              <Button onClick={handleAction} size="xs" type="button" variant="outline">
                {actionText}
              </Button>
            ) : null}
            <Button onClick={handleDismiss} size="xs" type="button" variant="default">
              {dismissText}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default SFHelpPopup
